import {
    RCT, DAZE, OBSERVATIONAL_MEASURE, LN,
} from '../../constants/measure';
import * as apiActions from '../api/apiActions';

class DNOServiceClient {
    static NAME = 'dno';

    static MEASURES = [OBSERVATIONAL_MEASURE.DORF, OBSERVATIONAL_MEASURE.DAZE, LN.MEASURE];

    static MEASURES_WITH_FORM_VERSION = [RCT];

    static URL = '/lanterndno/v01';

    static MOCKED_URL = '/mocked-lanterndno/v01';

    static CONTENT_ENTITY = {
        QUESTIONS: 'questions',
        PRACTICE: 'practice',
        MODEL: 'model',
    };

    static RESULT_OPERATION = {
        ADD_RESPONSE: 'add_response',
        MARK_COMPLETE: 'add_response_with_complete',
        MARK_DISCONTINUED: 'mark_discontinued',
    };

    static CLIENT_TO_SERVER_MEASURE_NAME = {
        [RCT]: 'RCT',
        [DAZE]: 'Daze',
        [LN.NAME]: 'LN',
    };

    /** Constructs request url to Lantern DNO mocked service based on the passed config object. */
    static constructMockedFormEndpoint(measure) {
        let endpoint = `${DNOServiceClient.MOCKED_URL}`;

        if (measure) {
            endpoint += `/${measure}`;
        }

        return endpoint;
    }

    /**
    * Constructs request url to Lantern DNO service based on the passed config object.
    * config may include:
        - measure (flm, daze etc.)
        - measureType (accuracy | fluency | comprehension)
        - formId
        - grage
        - period
    */
    static constructFormEndpoint({
        measure, measureType, formId, grade, period, formVersion,
    }) {
        let endpoint = `${DNOServiceClient.URL}`;

        if (measure) {
            endpoint += `/${measure}`;
        }

        if (measureType) {
            endpoint += `/${measureType}`;
        }

        endpoint += '/forms';

        if (formId) {
            endpoint += `/${formId}`;

            // no need to filter by grade/period if unique id is provided
            return endpoint;
        }

        const params = [];
        if (grade) {
            params.push(`grade=${grade}`);
        }
        if (period) {
            params.push(`period=${period}`);
        }
        if (formVersion) {
            params.push(`form_version=${formVersion}`);
        } else if (DNOServiceClient.MEASURES_WITH_FORM_VERSION.includes(measure)) {
            params.push('form_version=0');
        }
        if (params.length > 0) {
            endpoint += `?${params.join('&')}`;
        }

        return endpoint;
    }

    /**
     * Construct and return the URL API endpoint for a list of results
     */
    static constructResultListEndpoint({
        studentId, measure, grade, period, schoolYear,
        withItemLevel,
    } = {}) {
        let endpoint = `${DNOServiceClient.URL}/results`;
        const params = [];
        if (measure) {
            params.push(`measure=${DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[measure]}`);
        }
        if (studentId) {
            params.push(`student_uids=${studentId}`);
        }
        if (grade) {
            params.push(`grade=${grade}`);
        }
        if (period) {
            params.push(`period=${period}`);
        }
        if (schoolYear) {
            params.push(`school_year=${schoolYear}`);
        }
        if (withItemLevel) {
            params.push('with_item_level=True');
        }
        if (params.length > 0) {
            endpoint += `?${params.join('&')}`;
        }
        return endpoint;
    }

    /**
     * Construct and return the URL API endpoint for a given resultId
     * @param {str} resultId
     */
    static constructResultEndpoint(resultId) {
        return `${DNOServiceClient.URL}/results/${resultId}`;
    }

    /**
     * Sends DELETE request for result endpoint
     * @param {str} resultId - id of the result to DELETE
     */
    static deleteStudentResult = (resultId) => (dispatch) => {
        const endpoint = DNOServiceClient.constructResultEndpoint(resultId);
        return dispatch(apiActions.makeApiDeleteRequest(endpoint));
    };

    /**
     * Sends GET request to measure specific forms endpoint
     * and allows to retry the request if it fails
     */
    static getMeasureForm = (measure, measureType, formId, retryActionName) => (dispatch) => {
        const config = {
            measure,
            measureType,
            formId,
        };
        const endpoint = DNOServiceClient.constructFormEndpoint(config);
        return dispatch(apiActions.makeRetriableGetRequest(endpoint, retryActionName))
            .then((response) => {
                if (response && response.form_id){
                    return response;
                }
                return response;
            });
    };

    /**
     * Sends GET request to endpoint with provided parameters
     * @param {str} measure - (optional) specified DNO measure
     * @param {str} grade - (optional) student's grade
     * @param {str} period - (optional) BOY | MOY | EOY
     * @returns {JSON} server response
     */
    static getForm = (measure, measureType, grade, period, retryActionName) => (dispatch) => {
        const config = {
            measure,
            measureType,
            grade,
            period,
        };

        const endpoint = DNOServiceClient.constructFormEndpoint(config);
        return dispatch(apiActions.makeRetriableGetRequest(endpoint, retryActionName));
    };

    /**
     * Sends GET request to measure specific mocked forms endpoint
     */
    static getMeasureMockedForm = (measure) => async (dispatch) => {
        const endpoint = DNOServiceClient.constructMockedFormEndpoint(measure);
        const response = await dispatch(apiActions.makeApiGetRequest(endpoint));
        return response;
    };
}

export default DNOServiceClient;
